/**
 * EditorResultPanel — Displays suggestions with accept/reject per suggestion
 *
 * Two modes:
 * - Pending: checkboxes, Accept All / Accept Selected buttons
 * - Accepted: read-only view showing what was changed, with "Accepted" badge
 */

import { useState } from 'react'
import { Check, CheckCheck, ArrowLeft, CheckCircle, RotateCcw } from 'lucide-react'
import type { EditorTypeKey, EditorResultData, EditorSuggestionData } from '@/lib/api'
import { EDITOR_PERSONAS } from '@/lib/api'

interface EditorResultPanelProps {
  editorType: EditorTypeKey
  result: EditorResultData
  onApplySelected: (indices: number[]) => Promise<void>
  onAdoptAll: () => Promise<void>
  onClose: () => void
  onRerun?: () => void
}

function getSeverityBadge(severity: EditorSuggestionData['severity']) {
  const styles: Record<string, string> = {
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    major: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    minor: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    style: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  }
  return styles[severity] || styles.style
}

export function EditorResultPanel({
  editorType,
  result,
  onApplySelected,
  onAdoptAll,
  onClose,
  onRerun,
}: EditorResultPanelProps) {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set(result.suggestions.map((_, i) => i))
  )
  const [applying, setApplying] = useState(false)

  const persona = EDITOR_PERSONAS.find(p => p.key === editorType)
  const isAccepted = !!result.acceptedAt

  const toggleIndex = (i: number) => {
    if (isAccepted) return
    setSelectedIndices(prev => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }

  const selectAll = () => setSelectedIndices(new Set(result.suggestions.map((_, i) => i)))
  const selectNone = () => setSelectedIndices(new Set())

  const handleApplySelected = async () => {
    if (selectedIndices.size === 0) return
    setApplying(true)
    try {
      await onApplySelected(Array.from(selectedIndices))
    } finally {
      setApplying(false)
    }
  }

  const handleAdoptAll = async () => {
    setApplying(true)
    try {
      await onAdoptAll()
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-white/80 dark:bg-surface-900/80 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="text-lg">{persona?.emoji}</span>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {persona?.displayName} Results
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Score: {result.score}/10
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isAccepted && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                <CheckCircle className="w-3 h-3" />
                Accepted
              </span>
            )}
            <span className="text-xs text-gray-400">
              {result.suggestions.length} suggestion{result.suggestions.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        {/* Summary */}
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
          {result.summary}
        </p>
      </div>

      {/* Action bar */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-white/10 flex items-center gap-2 bg-gray-50 dark:bg-surface-800 flex-shrink-0">
        {isAccepted ? (
          /* Accepted state — show confirmation + re-run option */
          <>
            <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle className="w-4 h-4" />
              <span className="font-medium">
                {result.acceptedCount === result.suggestions.length
                  ? 'All changes accepted'
                  : `${result.acceptedCount} of ${result.suggestions.length} suggestions applied`
                }
              </span>
              <span className="text-gray-400 dark:text-gray-500">
                &middot; {new Date(result.acceptedAt!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className="flex-1" />
            {onRerun && (
              <button
                onClick={onRerun}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/15 rounded-md hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Re-run Editor
              </button>
            )}
          </>
        ) : (
          /* Pending state — accept buttons */
          <>
            <button
              onClick={handleAdoptAll}
              disabled={applying || result.suggestions.length === 0}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              <CheckCheck className="w-3 h-3" />
              Accept All
            </button>
            <button
              onClick={handleApplySelected}
              disabled={applying || selectedIndices.size === 0}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              <Check className="w-3 h-3" />
              Accept Selected ({selectedIndices.size})
            </button>
            <div className="flex-1" />
            <button onClick={selectAll} className="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline">
              Select All
            </button>
            <button onClick={selectNone} className="text-[10px] text-gray-500 hover:underline">
              Deselect All
            </button>
          </>
        )}
      </div>

      {/* Suggestions list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {result.suggestions.length === 0 ? (
          <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-12">
            No suggestions needed. The document scored {result.score}/10 on this dimension.
          </div>
        ) : (
          result.suggestions.map((suggestion, i) => (
            <SuggestionCard
              key={i}
              index={i}
              suggestion={suggestion}
              selected={isAccepted || selectedIndices.has(i)}
              accepted={isAccepted}
              onToggle={() => toggleIndex(i)}
            />
          ))
        )}
      </div>

      {/* Footer: model info */}
      <div className="px-4 py-2 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-surface-800 flex-shrink-0">
        <div className="flex items-center justify-between text-[10px] text-gray-400">
          <span>Model: {result.model}</span>
          <span>
            {result.inputTokens.toLocaleString()} in / {result.outputTokens.toLocaleString()} out &middot; {(result.durationMs / 1000).toFixed(1)}s
          </span>
        </div>
      </div>
    </div>
  )
}

// ── Suggestion Card ──────────────────────────────────────────────────────────

function SuggestionCard({
  index,
  suggestion,
  selected,
  accepted,
  onToggle,
}: {
  index: number
  suggestion: EditorSuggestionData
  selected: boolean
  accepted: boolean
  onToggle: () => void
}) {
  return (
    <div
      className={`rounded-lg border p-3 transition-colors ${
        accepted
          ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-800/40 dark:bg-emerald-900/5'
          : selected
            ? 'border-indigo-300 bg-indigo-50/50 dark:border-indigo-500/50 dark:bg-indigo-900/10 cursor-pointer'
            : 'border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 cursor-pointer'
      }`}
      onClick={!accepted ? onToggle : undefined}
    >
      <div className="flex items-start gap-2">
        {/* Checkbox or accepted check */}
        {accepted ? (
          <div className="mt-0.5 w-4 h-4 rounded-full bg-emerald-500 flex-shrink-0 flex items-center justify-center">
            <Check className="w-2.5 h-2.5 text-white" />
          </div>
        ) : (
          <div className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
            selected
              ? 'bg-indigo-600 border-indigo-600'
              : 'border-gray-300 dark:border-white/20'
          }`}>
            {selected && <Check className="w-3 h-3 text-white" />}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Location + severity */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] text-gray-400 font-mono">#{index + 1}</span>
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${getSeverityBadge(suggestion.severity)}`}>
              {suggestion.severity}
            </span>
            {suggestion.location && (
              <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                {suggestion.location}
              </span>
            )}
            {accepted && (
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium ml-auto">
                Applied
              </span>
            )}
          </div>

          {/* Diff view */}
          <div className="space-y-1 mt-2">
            <div className={`border rounded px-2 py-1 ${
              accepted
                ? 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10'
                : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800/30'
            }`}>
              <span className={`text-xs ${accepted ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-red-700 dark:text-red-400 line-through'}`}>
                {suggestion.original}
              </span>
            </div>
            <div className={`border rounded px-2 py-1 ${
              accepted
                ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/30'
                : 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/30'
            }`}>
              <span className="text-xs text-emerald-700 dark:text-emerald-400">{suggestion.replacement}</span>
            </div>
          </div>

          {/* Reason */}
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 leading-relaxed">
            {suggestion.reason}
          </p>
        </div>
      </div>
    </div>
  )
}
